/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/utils/googleMapsLoader.ts
 * Release: 2.1.0 - Loader dinamico per Google Maps
 * Descrizione: Utility per caricare dinamicamente Google Maps API
 */

import { GOOGLE_MAPS_CONFIG } from '../lib/config';

declare global {
  interface Window {
    initGoogleMaps: () => void;
  }
}

let isLoading = false;
let isLoaded = false;
const callbacks: (() => void)[] = [];

export const loadGoogleMaps = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Se Google Maps è disabilitato
    if (GOOGLE_MAPS_CONFIG.DISABLED || !GOOGLE_MAPS_CONFIG.API_KEY) {
      console.log('[GoogleMapsLoader] Google Maps disabilitato o API key mancante');
      reject(new Error('Google Maps disabilitato'));
      return;
    }

    // Se è già caricato
    if (isLoaded && window.google && window.google.maps) {
      console.log('[GoogleMapsLoader] Google Maps già caricato');
      resolve();
      return;
    }

    // Aggiungi callback alla lista
    callbacks.push(() => resolve());

    // Se sta già caricando, aspetta
    if (isLoading) {
      console.log('[GoogleMapsLoader] Caricamento già in corso, aspetto...');
      return;
    }

    console.log('[GoogleMapsLoader] Inizio caricamento Google Maps API');
    isLoading = true;

    // Callback globale per l'inizializzazione
    window.initGoogleMaps = () => {
      console.log('[GoogleMapsLoader] Google Maps API caricato con successo');
      isLoaded = true;
      isLoading = false;
      
      // Esegui tutti i callback
      callbacks.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error('[GoogleMapsLoader] Errore nel callback:', error);
        }
      });
      
      // Pulisci la lista dei callback
      callbacks.length = 0;
    };

    // Crea lo script tag
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_CONFIG.API_KEY}&libraries=places&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;

    script.onerror = () => {
      console.error('[GoogleMapsLoader] Errore nel caricamento di Google Maps API');
      isLoading = false;
      reject(new Error('Errore nel caricamento di Google Maps'));
    };

    // Aggiungi lo script al DOM
    document.head.appendChild(script);
    
    // Timeout di sicurezza
    setTimeout(() => {
      if (isLoading) {
        console.error('[GoogleMapsLoader] Timeout nel caricamento di Google Maps API');
        isLoading = false;
        reject(new Error('Timeout nel caricamento di Google Maps'));
      }
    }, 15000); // 15 secondi di timeout
  });
};

export const isGoogleMapsLoaded = (): boolean => {
  return isLoaded && !!(window.google && window.google.maps && window.google.maps.places);
};

export const waitForGoogleMaps = (): Promise<void> => {
  if (isGoogleMapsLoaded()) {
    return Promise.resolve();
  }
  
  return loadGoogleMaps();
};